import React, { useContext } from 'react';
import { Button } from '@material-ui/core';
import { useMutation, useQueryClient } from 'react-query';
import AppTable from '../../basic/AppTable';
import { SerialNumbersCtx } from '../../../pages/SerialNumbersPage';
import { useAlerts } from '../../../hooks/alerts-hooks';
import { useQueryParams } from '../../../hooks/atoms/query-params-hook';
import CollectionKeys from '../../../hooks/molecules/collection-keys';
import axiosClient from '../../../http/axios-client';

const SerialNumbersUploadPreview = (props) => {
  const { dataCSV, setDataCSV } = useContext(SerialNumbersCtx);
  const { alertSuccess, alertError } = useAlerts();

  const paginationWithoutCount = useQueryParams();

  const queryClient = useQueryClient();

  const {
    mutateAsync: uploadSerialNumbers,
    isLoading: serialNumbersUploading,
  } = useMutation(
    [ CollectionKeys.SERIAL_NUMBER, 'UPLOAD' ],
    (payload) => {
      return axiosClient.post(`${ CollectionKeys.SERIAL_NUMBER }/new-upload`, payload);
    },
    {
      onSettled() {
        return queryClient.invalidateQueries([ `${ CollectionKeys.SERIAL_NUMBER }/thematic-package/${ props.thematicPackageId }` ]);
      },
    },
  );

  if (!dataCSV) {
    return <h5>No data to preview</h5>;
  }

  const { rows, columns } = dataCSV;

  const data = rows.map((r) => {
    return r.reduce((acc, attr) => {
      acc[attr.Key] = attr.Value;
      return acc;
    }, {});
  });

  const packageId = props.thematicPackageId;

  const handleUpload = () => {
    const dataToUpload = rows.map((r) => {
      const metadata = r.reduce((acc, attr) => {
        if ('serial' === attr.Key || 'upgradable' === attr.Key
          || 'Report paid' === attr.Key || 'Kit paid' === attr.Key) { return acc; }
        acc[attr.Key] = attr.Value;
        return acc;
      }, {});
      const { Value: serial } = r.find((attr) => { return 'serial' === attr.Key; });
      let upgradable = r.find((attr) => { return 'upgradable' === attr.Key; })?.Value;
      const reportPaid = r.find((attr) => { return 'Report paid' === attr.Key; })?.Value;
      const kitPaid = r.find((attr) => { return 'Kit paid' === attr.Key; })?.Value;
      const country = props?.locationFilter?.country;

      // upgradable manipulation for "WL": it should be true by default
      if (/WL/.test(serial) && (upgradable === undefined || upgradable.toLowerCase() !== 'false')) {
        upgradable = 'true';
      }

      return {
        serial,
        is_upgradeable: 'true' === upgradable || 'True' === upgradable || 'TRUE' === upgradable,
        ...(reportPaid ? { report_payment_status: reportPaid } : []),
        ...(kitPaid ? { kit_payment_status: kitPaid } : []),
        ...(country ? { country } : { country: 'GR' }),
        thematic_package: packageId,
        metadata,
      };
    });

    let wrongRow;

    if ('3-in-1' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^WL\d{6}$/.test(s.serial);
      });
    } else if ('Biological Age' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^BA\d{6}$/.test(s.serial);
      });
    } else if (('Cardio Health' === props.thematicPackageName || 'IDNA Cardio Health' === props.thematicPackageName) && 'GR' !== props?.locationFilter?.country) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^CE\d{6}$/.test(s.serial);
      });
    } else if ('Cardio Health' === props.thematicPackageName || 'IDNA Cardio Health' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^CH\d{6}$/.test(s.serial);
      });
    } else if ('NutriGenetix' === props.thematicPackageName
      && 'GR' !== props?.locationFilter?.country) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^NE\d{6}$/.test(s.serial);
      });
    } else if ('NutriGenetix' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^NU\d{6}$/.test(s.serial);
      });
    } else if ('NutriGenetix-Laboratories' === props.thematicPackageName
      && 'GR' !== props?.locationFilter?.country) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^NE\d{6}$/.test(s.serial);
      });
    } else if ('NutriGenetix-Laboratories' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^NG\d{6}$/.test(s.serial);
      });
    } else if ('Nutrition' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^NT\d{6}$/.test(s.serial);
      });
    } else if ('PGx' === props.thematicPackageName
      && 'GR' !== props?.locationFilter?.country) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^IE\d{6}$/.test(s.serial);
      });
    } else if ('PGx' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^ID\d{6}$/.test(s.serial);
      });
    } else if ('Sports' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^SP\d{6}$/.test(s.serial);
      });
    } else if ('Vitamins' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^VS\d{6}$/.test(s.serial);
      });
    } else if ('Vitamins Upgrade' === props.thematicPackageName) {
      wrongRow = dataToUpload.find((s) => {
        if (s?.serial === undefined) { return false; }
        return !/^VR\d{6}$/.test(s.serial);
      });
    }

    if (wrongRow) {
      alertError(`Serial number ${ wrongRow.serial } doesn't match the thematic package or doesn't have the correct format with category prefix followed by 6 digits.`);
      return undefined;
    }

    props.setLoading(true);

    return uploadSerialNumbers(dataToUpload)
      .then(() => {
        alertSuccess('Serial numbers uploaded');
        setDataCSV(null);
      })
      .catch((err) => {
        alertError(JSON.stringify(err));
      }).finally(() => {
        props.setLoading(false);
      });
  };

  return (
    <>
      <AppTable
        columns={ columns }
        data={ data?.slice(paginationWithoutCount.page * paginationWithoutCount.limit, (paginationWithoutCount.page + 1) * paginationWithoutCount.limit) }
        paginationProps={ { ...paginationWithoutCount, count: data?.length } }
      />
      <Button
        style={ { background: '#93C120', color: 'white' } }
        disabled={ serialNumbersUploading }
        onClick={ handleUpload }
      >
        Upload
      </Button>
    </>
  );
};

export default SerialNumbersUploadPreview;

